import Vue from "vue";
import $ from "jquery";
import { playerService } from "./player-service";
import jPlayer from "jplayer";
import ToastrType from "./toastr-types.interface";

let latestStreamRequest = 0;

// Init PlayerService for global use.
export const audioService = new Vue({
  data() {
    return {
      jp_instance: null,
      _initialisationComplete: false,
      debug: false,
      paused: true,
      progressPercent: null,
      muted: false,
      duration: null,
      seek: null,
      error: null,
      loading: null,
      canplayTimestamp: null,
    };
  },

  methods: {
    /**
     * Public facing initialise.
     */
    initialise() {
      if (this.debug) {
        console.log("[Audio Service]", "Public Initialise");
      }

      // Prevent multiple initialisations from the public facing init method.
      if (!this._initialisationComplete) {
        $(document).ready(() => {
          this._initialise();
        });
      }
    },

    /**
     * The real initialise function, when the page has loaded all the assets and is 'ready'
     * @private
     */
    _initialise() {
      if (this.debug) {
        console.log("[Audio Service]", "Private _initialise");
      }

      // Setup the Jplayer instance.
      this.jp_instance = $("#bb-jplayer").jPlayer({
        ready: () => {
          this.onJpReady();
        },
        cssSelectorAncestor: "#bb-jplayer-wrapper",
        swfPath: "/vendor/jplayer/jplayer",
        supplied: "mp3",
        useStateClassSkin: true,
        autoBlur: false,
        preload: "auto",
        smoothPlayBar: true,
        keyEnabled: true,
        keyBindings: this.getJpKeybindings(),
        remainingDuration: true,
        toggleDuration: true,
        volume: 1,
        timeupdate: this.onJpTimeupdated,
        loadstart: this.onJpLoadstart,
        canplay: this.onJpCanplay,
        error: this.onJpError,
        ended: this.onJpEnded,
        volumechange: this.onJpVolumechange,
        pause: this.onJpPause,
        play: this.onJpPlay,
      });
    },

    /* BEGIN JP EVENTS */

    /**
     * As track plays, the 'timeupdate' event is processed below.
     * @param event
     * @note: Internal JP event: 'timeupdated' runs at: 4Hz
     */
    onJpTimeupdated(event) {
      this.progressPercent = event.jPlayer.status.currentPercentAbsolute;
      this.duration = event.jPlayer.status.duration;
      this.seek = event.jPlayer.status.currentTime;
      this.$emit(
        "audio-service-progress-percent-updated",
        this.progressPercent,
      );
    },

    /**
     * Handles what happens when the loadstart event happens in jplayer.
     * @param event
     * @note: Internal JP event: 'loadstart'
     */
    onJpLoadstart(event) {
      if (this.debug) {
        console.log("[Audio Service]", "onJpLoadstart", event);
      }

      window.audioPlayerStatus = "loading";
      this.loading = true;
    },

    /**
     * Handles what happens when the canplay event happens in jplayer.
     * @param event
     * @note: Internal JP event: 'canplay'
     */
    onJpCanplay(event) {
      if (this.debug) {
        console.log("[Audio Service]", "canplay", event);
      }

      window.audioPlayerStatus = "loaded";
      this.error = false;
      this.loading = false;
      this.canplayTimestamp = Date.now();
    },

    /**
     * Handles what happens when the ended event happens in jplayer.
     * @param event
     * @note: Internal JP event: 'ended'
     */
    onJpEnded(event) {
      if (this.debug) {
        console.log("[Audio Service]", "ended", event);
      }
    },

    /**
     * Handles what happens when the volume change event happens in jplayer.
     * @param event
     * @note: Internal JP event: 'volumechange'
     */
    onJpVolumechange(event) {
      if (this.debug) {
        console.log("[Audio Service]", "volumechange", event);
      }

      this.muted = event.jPlayer.options.muted;
    },

    /**
     * Handles what happens when the ended event happens in jplayer.
     * @param event
     * @note: Internal JP event: 'pause'
     */
    onJpPause(event) {
      if (this.debug) {
        console.log("[Audio Service]", "pause", event);
      }

      this.paused = true;
    },

    /**
     * Handles what happens when the ended event happens in jplayer.
     * @param event
     * @note: Internal JP event: 'play'
     */
    onJpPlay(event) {
      if (this.debug) {
        console.log("[Audio Service]", "play", event);
      }

      this.paused = false;
    },

    /**
     * Handles what happens when the error event happens in jplayer.
     * @param event
     * @note: Internal JP event: 'error'
     */
    onJpError(event) {
      if (this.debug) {
        console.error("[Audio Service]", "Error", event);
      }

      window.audioPlayerStatus = "error";
      this.error = true;

      this.$emit("audio-service-jp-error", event);
    },

    /**
     * Runs when the JP itself is initialised fully.
     * @note: Internal JP event: 'ready'
     */
    onJpReady() {
      if (this.debug) {
        console.log("[Audio Service]", "onJpReady");
      }
    },

    /* END JP EVENTS */

    /**
     * Runs when the JP itself is initialised fully.
     */
    setTrack(trackUrl, trackId, autoplay = true) {
      // Little bit of validation/proofing.
      if (typeof trackUrl != "string") {
        console.error(
          "[Audio Service]",
          "Invalid Track URL provided.",
          trackUrl,
          autoplay,
        );
        return;
      }

      if (this.debug) {
        console.log("[Audio Service]", "Set Track", trackUrl, autoplay);
      }

      this.jp_instance.jPlayer("setMedia", {
        mp3: trackUrl,
      });

      window.nowPlayingId = trackId;

      if (autoplay) {
        this.play();
      } else {
        this.pause();
      }
    },

    /**
     * Updates the seek of the track, set in percentage.
     *
     * @param percent
     */
    setTrackSeekPercent(percent) {
      this.jp_instance.jPlayer("playHead", percent);
    },

    /**
     * Toggle between pause & played state.
     */
    togglePlay() {
      playerService.$emit("show-random-track-info");

      if (this.paused) {
        this.play();
      } else {
        this.pause();
      }
    },

    /**
     * Toggle between pause & muted state.
     */
    toggleMute() {
      if (!this.muted) {
        this.mute();
      } else {
        this.unmute();
      }
    },

    /**
     * Updates jplayer volume.
     *
     * @param volume
     */
    setVolume(volume) {
      this.jp_instance.jPlayer("volume", volume);

      if (volume == 0) {
        this.mute();
      } else {
        this.unmute();
      }
    },

    /**
     * Pauses the audioplayer, great description.
     */
    pause() {
      this.jp_instance.jPlayer("pause");
      this.paused = true;
    },

    /**
     * Plays the audioplayer, great description.
     */
    play() {
      this.jp_instance.jPlayer("play");
      this.paused = false;
    },

    /**
     * Mutes the audio, great description.
     */
    mute() {
      this.jp_instance.jPlayer("mute");
    },

    /**
     * Unmutes the audio, great description.
     */
    unmute() {
      this.jp_instance.jPlayer("unmute");
    },

    /**
     * Returns the JSON for setting up what keys control what actions
     * @note: https://keycode.info for keycode help
     */
    getJpKeybindings() {
      return {
        play: {
          key: 32,
          fn: function (f) {},
        },
        muted: {
          key: 77,
          fn: function (f) {},
        },
      };
    },

    /**
     * Throws event that the player-app catches, passes it down to
     * audio-player and changes the now playing info.
     *
     * @param track
     * @returns {boolean}
     */
    stream: async function (track, isRandom = false) {
      // Check this is a valid track and that we have an id we can use to obtain the streaming data.
      if (typeof track === "undefined" || typeof track.id === "undefined") {
        if (window.adminProjectShare == true) {
          playerService.toast(
            "[Audio Service] Error: Could not stream track. Invalid track given.",
            ToastrType.Error,
          );
        }

        return false;
      }

      // let sourceUrl = `${AppBaseUrl}/api/tracks/${track.id}/stream?timestamp=${Date.now()}`;
      //
      // if (isRandom) {
      //   sourceUrl = `${AppBaseUrl}/api/tracks/${
      //     track.id
      //   }/stream-random?timestamp=${Date.now()}`;
      // }

      // const apiUrl = `/api/tracks/${track.id}/get-signed-url`;
      //
      // fetch(apiUrl)
      //   .then((response) => response.json())
      //   .then((data) => {
      //     // Retrieve the signed URL from the API response
      //     // Set the sourceUrl variable with the signed URL
      //     sourceUrl = data.signedUrl;
      //   })
      //   .catch((error) => {
      //     console.error("Error fetching signed URL:", error);
      //   });

      const streamRequest = ++latestStreamRequest;
      const results = await fetch(`/api/tracks/${track.id}/get-signed-url`);
      const json = await results.json();

      // A slow response for an earlier click must not replace a newer track.
      if (streamRequest !== latestStreamRequest) {
        return false;
      }

      if (json.success) {
        let autoplay = !(
          typeof track.noAutoplay != "undefined" && track.noAutoplay == true
        );

        this.setTrack(json.signedUrl, track.id, autoplay);

        // Create a timestamp
        this.$emit("stream-track", {
          track: track,
          sourceUrl: json.signedUrl,
        });

        if (autoplay == false) {
          playerService.$emit("noautoplay-track-now-playing", track);
        }
      } else {
        playerService.toast(
          "[Audio Service] Error: " + json.message,
          ToastrType.Error,
        );
      }
    },
  },
});
